import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useSingleCompanyMode } from '@/hooks/useWorkspaceUtils';
import { 
  Bell, 
  User, 
  LogOut, 
  Building2, 
  ChevronDown,
  Loader2
} from 'lucide-react';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SearchableCompanySwitcher } from '@/components/company/SearchableCompanySwitcher';
import { VirtualizedCompanySwitcher } from '@/components/company/VirtualizedCompanySwitcher';
import { cn } from '@/lib/utils';

export const UnifiedHeader: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const {
    currentCompany,
    currentPersona,
    companies,
    companyPersonas,
    switchCompany,
    setPersona,
  } = useWorkspace();
  
  const isSingleCompanyMode = useSingleCompanyMode();
  const [isSwitching, setIsSwitching] = useState(false);

  // Map route paths to readable titles
  const getTitleFromPath = (path: string) => {
    const routes: Record<string, string> = {
      "/dashboard": "Dashboard",
      "/chat": "Marketing Mary",
      "/personas": "Buyer Personas",
      "/analysis": "Company",
      "/settings": "Settings",
      "/companies": "Projects",
    };
    
    // Handle persona chat routes
    if (path.includes('/chat/p/')) {
      return `Chat with ${currentPersona?.name || 'Persona'}`;
    }
    
    // Handle company detail routes
    if (path.startsWith("/companies/")) {
      return "Project Details";
    }
    
    // Handle workspace routes
    const workspaceMatch = path.match(/\/w\/[^/]+(\/.+)/);
    if (workspaceMatch && workspaceMatch[1]) {
      return routes[workspaceMatch[1]] || "Marketing Mary";
    }
    
    return routes[path] || "Marketing Mary";
  };

  // Get user initials for avatar fallback - memoized for performance
  const userInitials = useMemo(() => {
    if (profile?.full_name) {
      return profile.full_name
        .split(' ')
        .filter(Boolean)
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    }
    return (user?.email?.slice(0, 2) || 'U').toUpperCase();
  }, [profile?.full_name, user?.email]);

  // Determine which company switcher to use based on company count
  const SEARCHABLE_THRESHOLD = 10;
  const VIRTUALIZED_THRESHOLD = 50;
  const companyCount = companies?.length ?? 0;
  const companySwitcherType = companyCount >= VIRTUALIZED_THRESHOLD 
    ? 'virtualized' 
    : companyCount >= SEARCHABLE_THRESHOLD 
    ? 'searchable' 
    : 'simple';

  const handleCompanySwitch = (companyId: string) => {
    if (companyId === currentCompany?.id || isSwitching) return;
    
    setIsSwitching(true);
    
    // switchCompany already handles navigation and state updates
    // via the backend persistence and callbacks
    switchCompany(companyId);
    
    // The WorkspaceContext will handle navigation and update isSwitching
    // We just need to reset our local loading state after a reasonable time
    setTimeout(() => setIsSwitching(false), 1000);
  };

  const isOnChatRoute = location.pathname.includes('/chat');

  return (
    <>
      {/* Loading progress bar */}
      {isSwitching && (
        <Progress className="h-1 w-full absolute top-0 left-0 z-50" value={100} />
      )}
      
      <header className="border-b p-4 flex items-center justify-between bg-white relative">
        <div className="flex items-center gap-4">
          {/* Sidebar trigger and page title */}
          <SidebarTrigger />
          <h1 className="text-xl font-semibold">{getTitleFromPath(location.pathname)}</h1>
        </div>

        <div className="flex items-center gap-4">
          {/* Company Selector - Only show if there's a current company */}
          {currentCompany && (
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              {isSingleCompanyMode ? (
                <span className="font-medium text-sm">{currentCompany.name}</span>
              ) : companySwitcherType === 'virtualized' ? (
                <VirtualizedCompanySwitcher 
                  disabled={isSwitching}
                  onSwitch={handleCompanySwitch}
                />
              ) : companySwitcherType === 'searchable' ? (
                <SearchableCompanySwitcher 
                  disabled={isSwitching}
                  onSwitch={handleCompanySwitch}
                />
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={isSwitching}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1 px-2"
                    >
                      {isSwitching ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin" />
                          <span className="font-medium text-sm">Switching...</span>
                        </>
                      ) : (
                        <>
                          <span className="font-medium text-sm">{currentCompany.name}</span>
                          <ChevronDown className="h-3 w-3" />
                        </>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuLabel>Switch Project</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {companies.map((company) => (
                      <DropdownMenuItem
                        key={company.id}
                        onClick={() => handleCompanySwitch(company.id)}
                        className={cn(
                          'cursor-pointer',
                          company.id === currentCompany.id && 'bg-accent'
                        )}
                        disabled={isSwitching}
                      >
                        <Building2 className="mr-2 h-4 w-4" />
                        {company.name}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => navigate('/companies')}
                      className="cursor-pointer"
                      disabled={isSwitching}
                    >
                      <span className="text-muted-foreground">Manage Projects</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}

          {/* Persona Selector - Only show on chat routes */}
          {isOnChatRoute && currentCompany && companyPersonas?.length > 0 && (
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1 px-2"
                  >
                    <span className="font-medium text-sm">
                      {currentPersona ? currentPersona.name : 'Marketing Mary'}
                    </span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel>Switch Persona</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      setPersona(null);
                      navigate(`/w/${currentCompany.slug}/chat`);
                    }}
                    className={cn(
                      'cursor-pointer',
                      !currentPersona && 'bg-accent'
                    )}
                  >
                    <span className="font-medium">Marketing Mary</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {companyPersonas.map((persona) => (
                    <DropdownMenuItem
                      key={persona.id}
                      onClick={() => {
                        setPersona(persona);
                        navigate(`/w/${currentCompany.slug}/chat/p/${persona.id}`);
                      }}
                      className={cn(
                        'cursor-pointer',
                        persona.id === currentPersona?.id && 'bg-accent'
                      )}
                    >
                      <User className="mr-2 h-4 w-4" />
                      <div>
                        <div className="font-medium">{persona.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {persona.job_title}
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Right side: Notifications and User Menu */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Notifications">
              <Bell className="h-5 w-5" />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full" aria-label="User menu">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={profile?.avatar_url} alt="User avatar" />
                    <AvatarFallback>{userInitials}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{profile?.full_name || "User"}</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => signOut()}
                  className="text-red-600 cursor-pointer"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
    </>
  );
};